import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";
import api from "services/api";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { PUBLISH_STATUSES, PublishStatus } from "constants/models";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "utils/publish";
import { HealthConditionDetail } from "./HealthConditionStore";
import { HealthConditionDictionaryItem } from "./dictionary/HealthConditionDictionary";
import { TagDictionaryItem } from "./dictionary/TagDictionary";

const pathClaim = "/nutrition/api/v1";
export type ClaimDetail = {
  id: number;
  title: string;
  shortDescription?: string;
  description?: string;
  publishStatus?: PublishStatus;
  tags?: any[];
  healthConditions?: HealthConditionDetail[];
};

export type OptionalClaimDetail = {
  id?: number;
  title?: string;
  publishStatus?: PublishStatus;
};

export type Claim = {
  title: string;
  publishStatus?: PublishStatus;
};

export const ClaimModel = types.model({
  id: types.maybe(types.number),
  title: types.maybe(types.string),
  publishStatus: types.maybe(
    types.enumeration(Object.values(PUBLISH_STATUSES)),
  ),
});

export const ClaimDetailModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.string,
    description: types.maybeNull(types.string),
    shortDescription: types.maybeNull(types.string),
    publishStatus: types.maybe(
      types.enumeration(Object.values(PUBLISH_STATUSES)),
    ),
    healthConditions: types.maybe(types.array(HealthConditionDictionaryItem)),
    tags: types.maybe(types.array(TagDictionaryItem)),
  })
  .views(self => ({
    get formData() {
      const uniqueMap = new Map();
      self.healthConditions?.map(item => uniqueMap.set(item.id, item));
      const list = Array.from(uniqueMap.values());

      const uniqueTagMap = new Map();
      self.tags?.map((item: any) => uniqueTagMap.set(item.id, item));
      const tagList = Array.from(uniqueTagMap.values());

      return {
        ...self,
        healthConditions: list.map(({ id, title }) => ({
          id,
          name: title,
        })),
        tags: tagList.map(({ id, title }) => ({
          id,
          name: title,
        })),
      };
    },
  }));

const ClaimStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathClaim}/claim`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: ClaimDetailModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(ClaimDetailModel),
    isMoreLoading: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false),
    isManaging: types.optional(types.boolean, false),
    isPublishing: types.optional(types.boolean, false),
    isCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    updateItem(id: number, data: OptionalClaimDetail) {
      const { items } = getSnapshot(self);

      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);
          const { items } = yield api.get(
            `${pathClaim}/claim`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    fetchDetails: flow(function*(id?: number) {
      try {
        self.isLoading = true;
        const details = yield api.get(
          `${pathClaim}/claim/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    editItem: flow(function*(payload: ClaimDetail) {
      self.isManaging = true;

      try {
        return yield api.put(
          `${pathClaim}/claim/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },

    createItem: flow(function*(payload: Claim) {
      self.isManaging = true;

      try {
        const { id } = yield api.post(
          `${pathClaim}/claim`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),

    changeStatus: flow(function*(id: number, status: PublishStatus) {
      self.isPublishing = true;

      try {
        const newStatus: any = getNextPublishStatusWithoutScheduled(status);
        if (!newStatus) throw new Error("Invalid Status");

        const { success } = yield api.post(
          `${pathClaim}/claim/${id}/status`,
          {
            status: newStatus,
          },
          undefined,
          MSURL,
        );
        if (!success) throw new Error("Something failed in the request");
        self.updateItem(id, { publishStatus: newStatus });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isPublishing = false;
      }
    }),
  }));

export default ClaimStore.create();
